import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { SearchResultsProcessor } from '@formbird/shared';
import { ComponentDefinition, DocumentChangedInfo, FormDocument, SharedConstants, SearchOptions } from '@formbird/types';
import { ChangedDocumentService } from 'formbird-services';
import { FormbirdInjectorService } from '../../../../formbird-services/src/lib/services/injector/formbird-injector.service';
import { cloneDeep, isEqual, remove } from 'lodash';
import { debounceTime, switchMap } from 'rxjs/operators';
import { ComponentSetupService } from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import { AbstractDocumentComponent } from '../../../../formbird-sc-shared/src/lib/components/abstract-document.component';
import { sclHandlebars } from '../../../../formbird-sc-shared/src/lib/handlebars/sclHandlebars';
import { SclUtilUrlService } from '../../../../formbird-sc-shared/src/lib/scl-util-url.service';
import { DataService } from '../../../../formbird-services/src/lib/services/data/data.service';
import { RelatedDocumentQueries } from './related-document.queries';
import {NgSelectComponent} from '@ng-select/ng-select';

declare var $;

// extend the ComponentDefinition type with the additional fields for the component
interface RelatedDocumentComponentDefinition extends ComponentDefinition {
  urlOpenIn?: string;
  filter?: string;
  singleSelection?: boolean;
  relatedFields?: object;
  uniqueSelection?: boolean;
  fieldDefault?: object;
  newTemplate?: string;
  disableLink?: boolean;
  showDropDown?: boolean;
  defaultSearchProvider?: string;
  queryName?: string;
  searchOptions?: SearchOptions;
}

interface RelatedDocument extends FormDocument {
  parentDoc?: any;
}

@Component({
  templateUrl: './related-document.component.html',
  styleUrls: ['./related-document.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class RelatedDocumentComponent extends AbstractDocumentComponent<RelatedDocumentComponentDefinition> implements OnInit {
  @Input() docs: object = {};
  @Input() floatLabel: any;
  @Input() account: any;

  @ViewChild('scRelatedDocChild', {static: false}) relatedDocHTML: ElementRef;
  @ViewChild(NgSelectComponent) ngSelectComponent: NgSelectComponent;

  // service
  unsavedDocumentService: any;

  events: Event[] = [];

  values: any[] = [];

  items: any[];

  typeahead = new EventEmitter<string>();
  page = 0;
  loaded = 0;
  loading = false;
  hasSelected = false;
  searchText;
  errorMessage = null;
  results: any[];
  totalResults: any = 0;
  maxSelectedItems;
  uniqueSelection = false;
  relatedFields;
  isOpened = null;
  disableLink = false;
  options: any = {
    page: 0,
    pageSize: 50,
    text: '',
    from: null,
    // queryToBeFiltered: null,
    sourceFilter: {include: ['documentId', 'systemHeader.summaryName']}
  };

  isAdded = false;
  showDropDown = true;
  private dupItemList: any;
  selectedIndexToBeRemoved: any;
  selectedNgSelectClearElem: any;
  mandatory = false;
  searchClientService: any;

  constructor(
    protected cd: ChangeDetectorRef,
    private dataService: DataService,
    private sclUtilUrlService: SclUtilUrlService,
    protected changedDocumentService: ChangedDocumentService,
    protected componentSetupService: ComponentSetupService,
    private relatedDocumentQuries: RelatedDocumentQueries,
    protected formbirdInjectorService: FormbirdInjectorService,
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cd);

    this.unsavedDocumentService = this.formbirdInjectorService.get('UnsavedDocumentService');
    this.searchClientService = this.formbirdInjectorService.get('SearchClientService');
  }

  onComponentDefChanged(componentDefinition) {
    super.onComponentDefChanged(componentDefinition);
    if (componentDefinition.mandatory !== this.mandatory) {
      this.mandatory = componentDefinition.mandatory === true;
      this.detectChanges();
    }
  }

  onFieldValueChanged(fieldValue) {

    if (this.fieldValue === fieldValue) {
      return;
    }

    this.loading = false;
    this.fieldValue = fieldValue;
    const selectedValues = cloneDeep(this.fieldValue);
    if (!(typeof selectedValues === 'undefined' || selectedValues === null)) {
      // tslint:disable-next-line:prefer-for-of
      for (let indexSel = 0; indexSel < selectedValues.length; indexSel++) {
        selectedValues[indexSel].uniqueSelection = this.uniqueSelection;
      }
      this.fieldValue = selectedValues;
      this.selectedIndexToBeRemoved = selectedValues.length - 1;
    }
    if (this.formControl) {
      this.formControl.patchValue(this.fieldValue, {emitEvent: false, onlySelf: true});
    }

    this.detectChanges();

  }

  onControlValueChanged(value) {
    // do onChange instead
  }

  dispatchChanged(isInitValue?) {
    if (isInitValue) {
      const selectedValues = cloneDeep(this.fieldValue);
      // tslint:disable-next-line:prefer-for-of
      for (let indexSel = 0; indexSel < selectedValues.length; indexSel++) {
        selectedValues[indexSel].uniqueSelection = this.uniqueSelection;
      }
      this.fieldValue = selectedValues;
      this.selectedIndexToBeRemoved = selectedValues.length - 1;
    }
    const documentId = this.document.documentId;
    const documentChangedInfo: DocumentChangedInfo = {
      documentId: documentId,
      fieldName: this.fieldName,
      newValue: this.fieldValue,
      isInitValue: isInitValue
    };

    this.changedDocumentService.notifyDocumentChanged(documentChangedInfo);

  }

  ngOnInit() {

    this.shouldNotifyCompInitializedInSuper = false;

    const vm = this;

    this.floatLabel = this.floatLabel ? this.floatLabel : 'auto';
    this.disableLink = this.componentDefinition.disableLink;

    if (this.componentDefinition.singleSelection) {
      this.maxSelectedItems = 1;
    }

    if (typeof this.componentDefinition.showDropDown === 'undefined' || this.componentDefinition.showDropDown === null) {
      this.showDropDown = false;
    }else {
      this.showDropDown = this.componentDefinition.showDropDown;
    }

    if (typeof this.componentDefinition.uniqueSelection === 'undefined' || this.componentDefinition.uniqueSelection === null) {
      this.uniqueSelection = false;
    } else {
      this.uniqueSelection = this.componentDefinition.uniqueSelection;
    }

    super.ngOnInit();

    this.mandatory = this.componentDefinition.mandatory === true;

    if (this.componentDefinition.relatedFields) {
      this.relatedFields = this.componentDefinition.relatedFields;
      // tslint:disable-next-line:forin
      for (const key in this.relatedFields) {
        vm.options.sourceFilter.include.push(this.relatedFields[key]);
      }
    } else {
      this.relatedFields = {name: 'systemHeader.summaryName'};
    }

    if (this.componentDefinition.queryName) {
      vm.options.dataSource =  this.searchClientService.getDefaultSearchProvider(this);
      vm.options.queryName = this.componentDefinition.queryName;

      this.options.searchOfflineOnly = this.componentDefinition.searchOptions?.searchOfflineOnly;
    }

    const typeSub = this.typeahead
      .pipe(
        debounceTime(SharedConstants.DEFAULT_SEARCH_DELAY),
        switchMap(term => {
          vm.page = 0;
          vm.options.page = 0;
          vm.options.from = null;
          vm.hasSelected = false;

          if (term && term !== '') {
            vm.loading = true;
            vm.searchText = term;

            this.relatedDocumentQuries.processElasticQuery(vm, term, false);
            const searchProviderName = this.searchClientService.getDefaultSearchProvider(vm);

            if (this.searchClientService.isDexieQuery(vm)) {
              this.relatedDocumentQuries.processDexieQuery(vm, term, false);
            }

            return this.searchClientService.search(vm.options);
          } else {
            // when error occurs in searchService like blank text,
            // it doesn't work again
            const data = null;
            return Promise.resolve(data);
          }
        })
      )
      .subscribe(
        (data: any) => {
          if (data) {
            SearchResultsProcessor.processSearchResults(data,
              function elementProcessing(hit) {
              },
              function complete(hits, totalHits) {
                vm.totalResults = totalHits;
                vm.loaded = hits.length;

                const items = [];

                for (const doc of hits) {
                  let summaryName = '';
                  if (doc && doc.systemHeader && doc.systemHeader.summaryName) {
                    summaryName = doc.systemHeader.summaryName;
                  }
                  doc.name = doc.systemHeader.summaryName;
                  doc.uniqueSelection = vm.uniqueSelection;
                  delete doc.systemHeader;
                  items.push(doc);
                }

                vm.results = [...items];
                vm.isOpened = true;
                vm.detectChanges();
              });
          } else {
            vm.totalResults = 0;
            vm.loaded = 0;
            vm.results = [];
            vm.detectChanges();
          }

          vm.loading = false;
          vm.errorMessage = null;
        },
        err => {
          vm.results = [];
          vm.loading = false;
          vm.errorMessage = err.message;
          vm.detectChanges();
        }
      );

    this.subs.add(typeSub);

  }

  initializeFieldValue() {

    if (this.fieldValue) {
      const value = this.fieldValue;

      if ('string' === typeof value) {
          const item = {
            documentId: value,
            name: this.fieldName
          };

          // 15019: Angular Only: initialize for Parent Doc
          this.onFieldValueChanged([item]);
      }
      const selectedValues = cloneDeep(this.fieldValue);
      // tslint:disable-next-line:prefer-for-of
      for (let indexSel = 0; indexSel < selectedValues.length; indexSel++) {
        this.fieldValue[indexSel].uniqueSelection = this.uniqueSelection;
        selectedValues[indexSel].uniqueSelection = this.uniqueSelection;
      }
      this.fieldValue = selectedValues;
      this.selectedIndexToBeRemoved = selectedValues.length - 1;
      this.dispatchChanged(true);
    }
    setTimeout(() => {
      $('#divParent' + this.registeredControlName + ' div.ng-select-wrapper').addClass('ng-select-is-not-focused');
    });
  }

  getObjectNotWildcardSummaryName(mustObject) {
    const mustSelected = [];
    mustObject.map((key, index) => {
      let isFoundSummaryName = false;
      if (key.wildcard) {
        for (const sss in key.wildcard) {
          if (sss === 'systemHeader.summaryName.lcase') {
            isFoundSummaryName = true;
          } else {
            isFoundSummaryName = false;
          }
        }
      } else {
        isFoundSummaryName = false;
      }
      if (!isFoundSummaryName) {
        mustSelected.push(mustObject[index]);
      }
    });
    return mustSelected;
  }

  getData() {
    // const json = this.componentLibraryJSON;
    // const hits = json.default.data.hits.hits;
    const items = [];

    // for (const hit of hits) {
    //   const doc = hit._source;
    //   let summaryName = '';
    //   if (doc && doc.systemHeader && doc.systemHeader.summaryName) {
    //     summaryName = doc.systemHeader.summaryName;
    //   }
    //   const item = {
    //     documentId: doc.documentId,
    //     name: summaryName
    //   };
    //   items.push(item);
    // }

    return items;
  }

  onChange(newValue, onRemove) {
    if (newValue && this.componentDefinition.fieldDefault) {
      if (newValue.documentId) {
        this.dataService.getDocument(newValue.documentId)
          .then((doc) => {
            Object.keys(this.componentDefinition.fieldDefault).map((destFld) => {
              const srcFld = this.componentDefinition.fieldDefault[destFld];
              if (doc[srcFld]) {
                this.fieldDefaultValueChange(destFld, doc[srcFld]);
              }
            });
          }, (error) => {
            console.log('error setting default values from related document id: ' + newValue.documentId);
          });
      }
    }
    this.setValueInDocument(newValue, onRemove);
    this.isOpened = null;
    this.results = [];
    this.isAdded = true;
    this.detectChanges();
  }

  setValueInDocument(value, onRemove) {
    let fieldValue = this.fieldValue ? cloneDeep(this.fieldValue) : [];
    if (value && value.documentId) {
      let isDuplicateSelectedItem = false;
      if (this.uniqueSelection) {
        if ((fieldValue.length >= 1)) {
          fieldValue.map(doc => {
            if (value.name) {
              if (isEqual(value.name, doc.name)) {
                isDuplicateSelectedItem = true;
              }
              console.log(value.name, doc.name);
            } else {
              if (value.documentId) {
                let isDocObjectExist = false;
                if (doc) {
                  if (doc.documentId) {
                    isDocObjectExist = true;
                  }

                }
                if (isDocObjectExist) {
                  console.log(value.documentId, doc.documentId);
                  if (isEqual(value.documentId, doc.documentId)) {
                    isDuplicateSelectedItem = true;
                  }
                } else {
                  if (isEqual(value.documentId, doc.documentId)) {
                    isDuplicateSelectedItem = true;
                  }
                }
              }
            }

          });
        }
      }
      if (!isDuplicateSelectedItem) {

        if (!fieldValue) {
          fieldValue = [];
        }

        let formattedValue;
        formattedValue = {documentId: value.documentId};
        if (value.name) {
          formattedValue.name = value.name;
        } else if (value.systemHeader && value.systemHeader.summaryName) {
          formattedValue.name = value.systemHeader.summaryName;
        } else {
          formattedValue.name = 'Document ' + value.documentId;
        }
        formattedValue.uniqueSelection = this.uniqueSelection;
        if (this.relatedFields) {
          // tslint:disable-next-line:forin
          for (const key in this.relatedFields) {
            const val = this.relatedFields[key];
            if (value[val] !== undefined) {
              formattedValue[key] = value[val];
            }
          }
        }

        if (this.componentDefinition.singleSelection === true) {
          fieldValue = [];
        }

        fieldValue.push(formattedValue);
        this.fieldValue = fieldValue;
      } else {
        this.fieldValue = fieldValue;
      }
    } else {
      // This case is click remove on the relate document because the value has been formatted
      this.fieldValue = [];
    }
    this.valueChanged(this.fieldValue);
    this.selectedIndexToBeRemoved = this.fieldValue.length - 1;
  }

  onRemove(event, ngSelect) {
    this.fieldDefaultValueRemove();
    const value = event.value;
    if (this.uniqueSelection) {
      if (value && value.documentId && this.fieldValue) {
        const selectedValues = cloneDeep(this.fieldValue);
        const result = remove(selectedValues, {documentId: value.documentId});

        if (selectedValues.length === 0) { // remove this empty object out of saved document
          this.valueChanged(undefined);

          const documentListId = this.formParameters.unsavedDocumentListId;
          this.unsavedDocumentService.addDocumentToUnsavedList(this.document, documentListId);
        } else {
          this.fieldValue = selectedValues;

          this.valueChanged(this.fieldValue);
          this.detectChanges();
        }
      }
    } else {
      // tslint:disable-next-line:radix
      this.customClearItem(event, event.value, ngSelect, parseInt(cloneDeep(this.selectedIndexToBeRemoved)));
    }

  }

  async onClick(event) {
    setTimeout(() => {
      $('#divParent' + this.registeredControlName + ' div.ng-select-wrapper').removeClass('ng-select-is-not-focused');
      $('#divParent' + this.registeredControlName + ' div.ng-select-wrapper .ng-input input').focus();
    });
    const vm = this;
    const selectedCnt = this.fieldValue ? this.fieldValue.length : 0;

    if (selectedCnt >= this.maxSelectedItems) {
      event.stopPropagation();
      return;
    }

    if (!this.showDropDown) {
      event.stopPropagation();
      return;
    }

    if (this.disabled) {
      event.stopPropagation();
      return;
    }

    if (this.isAdded) {
      vm.isOpened = null;
      vm.loading = false;
      this.isAdded = false;
      return;
    }

    if (!vm.results || vm.results.length === 0) {
      vm.page = 0;
      vm.options.page = 0;
      vm.options.from = null;
      vm.loading = true;
      vm.hasSelected = false;

      this.relatedDocumentQuries.processElasticQuery(vm, null, true);
      const searchProviderName = this.searchClientService.getDefaultSearchProvider(this);
      if (this.searchClientService.isDexieQuery(vm)) {
        this.relatedDocumentQuries.processDexieQuery(vm, null, true);
      }

      console.log('text entered is ^' + vm.options.text + '^  Search Filter is ' + JSON.stringify(vm.options.filter));
      const data = await this.searchClientService.search(vm.options);
      if (data) {
        let items; items = [];
        SearchResultsProcessor.processSearchResults(data, (result) => {
          const rs = result;
          rs.name = rs.systemHeader?.summaryName;
          rs.uniqueSelection = vm.uniqueSelection;
          delete rs.systemHeader;
          items.push(rs);

        }, (results, totalHits) => {
          vm.totalResults = totalHits;
          vm.loaded = results.length;
          vm.results = items;
          vm.loading = false;
          vm.isOpened = this.showDropDown;
          vm.detectChanges();
        });

      } else {
        vm.totalResults = 0;
        vm.loaded = 0;
        vm.results = [];
        vm.isOpened = this.showDropDown;
        vm.loading = false;
        vm.detectChanges();
      }
    } else {
      vm.isOpened = this.showDropDown;
      vm.loading = false;
      vm.detectChanges();
    }
  }

  private processHandlebars(filter: string) {
    const handlebarsContext = {
      document: this.document,
      doc: this.document,
      account: this.account, // the account document from scope
      tpl: this.template,
      template: this.template,
      componentDefinition: this.componentDefinition // the current components configuration details
    };

    return sclHandlebars.processHandlebars(filter, null, handlebarsContext);
  }

  hoverHidePanel() {
    this.relatedDocHTML.nativeElement.classList.add('ng-hover');
  }

  inShowPanel() {
    // this.relatedDocHTML.nativeElement.classList.remove('ng-hover');
    if (this.showDropDown) {
      this.isOpened = true;
      const selectedCnt = this.fieldValue ? this.fieldValue.length : 0;
      if (selectedCnt >= this.maxSelectedItems) {
        this.isOpened = null;
      }
    } else {
      this.isOpened = null;
    }

    this.detectChanges();
  }

  outHidePanel() {
    // this.relatedDocHTML.nativeElement.classList.remove('ng-hover');
    setTimeout(() => {
      $('#divParent' + this.registeredControlName + ' div.ng-select-wrapper').addClass('ng-select-is-not-focused');
    });
    this.isOpened = null;
    this.results = [];
    this.detectChanges();
  }

  preventDropdown(ngSelect) {
    ngSelect.isOpen = false;
  }

  openLink(item, event, ngSelect) {
    this.isOpened = false;
    ngSelect.close();
    event.stopPropagation();
    this.preventDropdown(ngSelect);
    const openMode = this.componentDefinition.urlOpenIn ? this.componentDefinition.urlOpenIn : 'currentWindow';
    if (openMode !== 'currentWindow') {
      event.preventDefault();
    }
    if (!(event.shiftKey || event.ctrlKey || event.metaKey)) {
      this.sclUtilUrlService.openLink(this.getRelUrlLink(item), openMode, this);
    }
  }

  getRelUrlLink(item) {
    return '/form/' + item.documentId;
  }

  showTemplateDialog() {
    if (this.componentDefinition.newTemplate) {
      const link = 'form/' + this.componentDefinition.newTemplate;
      const openMode = this.componentDefinition.urlOpenIn ? this.componentDefinition.urlOpenIn : 'currentWindow';
      this.sclUtilUrlService.openLink(link, openMode, this);
    }
  }

  loadItemName(item) {
    if (item.name) {
      return item.name;
    }
    if (item.documentId) {
      return 'Document ' + item.documentId;
    }
  }

  dropDownClose() {
    this.searchText = '';
    this.options.text = '';
    this.results = [];
    this.isOpened = null;
    this.loading = false;
  }

  fieldDefaultValueChange(key, value) {
    const documentId = this.document.documentId;
    const documentChangedInfo: DocumentChangedInfo = {
      documentId: documentId,
      fieldName: key,
      newValue: value,
      isInitValue: false,
      formParameters: this.formParameters
    };

    this.changedDocumentService.notifyDocumentChanged(documentChangedInfo);
  }

  fieldDefaultValueRemove() {
    if (this.componentDefinition.fieldDefault) {
      Object.keys(this.componentDefinition.fieldDefault).map((destFld) => {
        this.fieldDefaultValueChange(destFld, undefined);
      });
    }
  }

  onClearAll() {

    if (this.componentDefinition.singleSelection) {
      this.fieldValue = undefined;
      this.selectedIndexToBeRemoved = 0;
    }
    this.valueChanged(undefined);

    this.fieldDefaultValueRemove();

    const documentListId = this.formParameters.unsavedDocumentListId;
    this.unsavedDocumentService.addDocumentToUnsavedList(this.document, documentListId);
  }

  customClearItem(event, item, ngSelect, itemIndex) {
    const self = this;
    // console.log('customClearItem', event, item, item.value, this.ngSelectComponent, itemIndex);
    setTimeout(() => {
      const itemList = cloneDeep(self.ngSelectComponent.itemsList);
      itemList._selectionModel.scRelatedDocumentComponent = self;
      self.ngSelectComponent.itemsList = itemList;
      self.dupItemList = itemList;
      // console.log(itemList);
      itemList._selectionModel.customUnselect(item, null, itemIndex, ngSelect);
       // const selected = itemList.selectedItems;
       // console.log('selectedValues before removeItem', selected);
    });

    if (this.componentDefinition.singleSelection) {
      self.fieldValue = [];
    }

    if (item && item.documentId && self.fieldValue) {
      // self.ngSelectComponent.clearItem(item);
      let selectedValues = cloneDeep(self.fieldValue);
      selectedValues = selectedValues.filter((elem, index) => index !== itemIndex);
      // console.log('customClearItem selectedValues after removed', selectedValues);
      self.fieldValue = selectedValues;
      self.selectedIndexToBeRemoved = selectedValues.length - 1;
      self.valueChanged(self.fieldValue);
    }

    if (!this.fieldValue || this.fieldValue.length === 0) {
      self.valueChanged(undefined);
    }

    self.detectChanges();
    this.cd.markForCheck();

    setTimeout(() => {
      $('#divParent' + this.registeredControlName + ' div.ng-select-wrapper').removeClass('ng-select-is-not-focused');
      $('#divParent' + this.registeredControlName + ' div.ng-select-wrapper .ng-input input').focus();
    });
  }


  clearSelectedItem(ngSelectClearElem: HTMLSpanElement, clear: any, item: any) {
    if (this.uniqueSelection) {
      clear(item);
    } else {
      this.selectedNgSelectClearElem = ngSelectClearElem;
      setTimeout(() => {
        $(this.ngSelectComponent.element).find('.ng-value-icon.left').map((index, elem) => {
          if (elem === this.selectedNgSelectClearElem) {
            this.selectedIndexToBeRemoved = index;
            // console.log('get SelectedIndex done!', elem, index);
          }
        });
        clear(item);
      });

    }
    if (this.componentDefinition.singleSelection) {
      this.fieldValue = undefined;
      this.selectedIndexToBeRemoved = 0;
    }
    this.valueChanged(this.fieldValue);
    setTimeout(() => {
      $('#divParent' + this.registeredControlName + ' div.ng-select-wrapper').removeClass('ng-select-is-not-focused');
      $('#divParent' + this.registeredControlName + ' div.ng-select-wrapper .ng-input input').focus();
    });
  }
}
